
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { useServiceProvider } from "@/hooks/useServiceProvider";
import { useServiceReviews } from "@/hooks/useServiceReviews";
import { useReviewStats } from "@/hooks/useReviewStats";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from '@/config/featureFlags';
import ServiceBreadcrumb from "@/components/service/ServiceBreadcrumb";
import ServiceLoading from "@/components/service/ServiceLoading";
import ServiceNotFound from "@/components/service/ServiceNotFound";
import ServiceDetails from "@/components/service/ServiceDetails";
import ServiceSidebar from "@/components/service/ServiceSidebar";
import { useEffect, useState } from "react";
import { debugLogger } from '@/utils/debug';
import { fetchProviderStatus } from "@/services/providerStatusService";
import { isUuidLike } from "@/services/providers/providerCatalogService";

const ServicePageContainer = () => {
  const { id: serviceIdFromParams } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const adsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ADS);

  // Fetch service provider data using our custom hook
  const {
    data: provider,
    isLoading: isLoadingProvider,
    error: providerError,
    refetch: refetchProvider
  } = useServiceProvider(serviceIdFromParams);

  const reviewProviderId = provider?.id ?? (serviceIdFromParams && isUuidLike(serviceIdFromParams) ? serviceIdFromParams : undefined);

  // Fetch reviews using our custom hook
  const {
    data: reviews = [],
    isLoading: isLoadingReviews,
    refetch: refetchReviews
  } = useServiceReviews(reviewProviderId);

  // Get the review stats directly from the reviews data
  const { averageRating, totalReviews } = useReviewStats(reviews);

  // State for subscription tier
  const [subscriptionTier, setSubscriptionTier] = useState('free_tier');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Effect to check if the provider has a premium subscription
  useEffect(() => {
    const checkProviderSubscription = async () => {
      if (!provider?.user_id) {
        setIsLoadingSubscription(false);
        setSubscriptionTier('free_tier'); // Ensure default state if no user_id
        return;
      }
      
      setIsLoadingSubscription(true); // Set loading true before fetch
      try {
        const status = await fetchProviderStatus(provider.user_id);
        setSubscriptionTier(status?.tier || 'free_tier');
      } catch (error) {
        console.error('Error in checkProviderSubscription:', error);
        setSubscriptionTier('free_tier');
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    
    if (provider) { // Only run if provider data is available
        checkProviderSubscription();
    }
  }, [provider?.user_id, provider]); // Depend on provider object as well

  // Log the service ID we're trying to fetch
  useEffect(() => {
    debugLogger.log("ServicePage - Current serviceIdFromParams:", serviceIdFromParams);
  }, [serviceIdFromParams]);

  // Check if the current user has already submitted a review
  const hasUserReviewed = Boolean(user && reviews.some(review => review.userId === user.id));
  
  const handleReviewSubmit = () => {
    refetchReviews();
    refetchProvider(); // Refetch provider to update stats like review count/rating
    toast.success("Your review has been submitted!");
  };
  
  const handleReviewUpdated = () => {
    refetchReviews();
    refetchProvider(); // Refetch provider for consistency
  };

  const isPremiumProvider = subscriptionTier !== 'free_tier';

  // Add detailed logging
  debugLogger.log("ServicePage - provider:", provider);
  debugLogger.log("ServicePage - provider status:", provider?.status);
  debugLogger.log("ServicePage - isAdmin:", isAdmin);
  debugLogger.log("ServicePage - reviews:", reviews);
  debugLogger.log("ServicePage - averageRating:", averageRating);
  debugLogger.log("ServicePage - totalReviews:", totalReviews);
  debugLogger.log("ServicePage - isLoadingProvider:", isLoadingProvider);
  debugLogger.log("ServicePage - providerError:", providerError);
  debugLogger.log("ServicePage - adsEnabled:", adsEnabled);

  // If we're still loading provider or subscription status, show loading state
  if (isLoadingProvider || (provider && isLoadingSubscription)) {
    return (
      <div className="min-h-screen bg-brand-mist px-4 py-8 md:py-12">
        <div className="mx-auto max-w-6xl">
          <ServiceBreadcrumb /> {/* Removed name and category props */}
          <ServiceLoading />
        </div>
      </div>
    );
  }

  // Handle no provider found or error cases
  if (providerError || !provider) {
    console.error("Service not found or error:", providerError);
    return (
      <div className="min-h-screen bg-brand-mist px-4 py-8 md:py-12">
        <div className="mx-auto max-w-6xl">
          <ServiceBreadcrumb /> {/* Removed name and category props */}
          <ServiceNotFound />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-mist px-4 py-8 md:py-12">
      <div className="mx-auto max-w-6xl">
        <ServiceBreadcrumb /> {/* Removed name and category props */}
        
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.68fr)_minmax(300px,0.32fr)] lg:items-start">
          <ServiceDetails 
            provider={provider} 
            reviews={reviews} 
            averageRating={averageRating} 
            totalReviews={totalReviews} 
            isLoadingReviews={isLoadingReviews} 
            onReviewUpdated={handleReviewUpdated}
            hasUserReviewed={hasUserReviewed} // Pass down
            serviceId={provider.id}         // Pass down
            isPremiumProvider={isPremiumProvider} // Pass down
            subscriptionTier={subscriptionTier} // Pass down new prop
            adsEnabled={adsEnabled} // Pass down
          />
          
          <ServiceSidebar 
            user={user} 
            serviceId={provider.id} 
            hasUserReviewed={hasUserReviewed} 
            onReviewSubmit={handleReviewSubmit} 
            providerName={provider.name} 
            whatsappNumber={provider.whatsapp_number}
            isPremiumProvider={isPremiumProvider} // Pass down
            adsEnabled={adsEnabled} // Pass down
          />
        </div>
      </div>
    </div>
  );
};

export default ServicePageContainer;
